// Quick Add: paste one or many links, review pre-filled cards, create drafts.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeftIcon, BoltIcon, CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import NavBar from "../../components/feature/NavBar";
import Footer from "../../components/feature/Footer";
import EntityPicker from "../../components/admin/EntityPicker";
import { inputClass, primaryButton, ProgressBar, secondaryButton, Section, StatusBadge } from "../../components/admin/ui";
import { useVideosWithEntities } from "../../hooks/useVideosWithEntities";
import { useTaxonomies } from "../../hooks/useTaxonomies";
import { usePageMeta } from "../../hooks/usePageMeta";
import { Entity, VideoStatus } from "../../types/cms";
import { TAXONOMY_LABELS, TaxonomyKey } from "../../lib/taxonomies";
import { suggestFromText, Suggestions } from "../../lib/autoTag";
import {
  brandNamesFromTitle,
  buildDuplicateIndex,
  domainsInText,
  EnrichedVideo,
  enrichVideoLink,
  matchEntity,
} from "../../lib/enrich";
import { isValidVideoInput, parseVideoSource } from "../../lib/videoSources";
import { saveVideo } from "../../lib/videoAdmin";
import { Timestamp } from "firebase/firestore";
import { notify } from "../../utils/notify";

type RowState = "loading" | "ready" | "duplicate" | "invalid" | "saving" | "saved" | "error";

interface Row {
  key: string;
  input: string;
  state: RowState;
  include: boolean;
  enriched?: EnrichedVideo;
  duplicateOf?: { id: string; title: string; status?: VideoStatus };
  title: string;
  enterpriseId: string;
  agencyId: string;
  brandHint?: string;
  studioHint?: string;
  classes: Record<TaxonomyKey, string[]>;
  suggestions: Suggestions;
  savedId?: string;
  error?: string;
}

const AUTO_ACCEPT = 0.5;

function splitLinks(text: string): string[] {
  const seen = new Set<string>();
  return text
    .split(/[\s,;]+/)
    .map((l) => l.trim())
    .filter((l) => l && /[./]/.test(l))
    .filter((l) => {
      const key = isValidVideoInput(l) ? parseVideoSource(l).sourceKey : l;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function Bookmarklet() {
  const ref = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    // React refuses javascript: URLs in JSX, so the href is set on the DOM node.
    const target = `${window.location.origin}/admin/quick-add?url=`;
    ref.current?.setAttribute(
      "href",
      `javascript:(()=>{window.open('${target}'+encodeURIComponent(location.href),'_blank')})()`,
    );
  }, []);
  return (
    // The href is injected above; this link is meant to be dragged, not clicked.
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <a
      ref={ref}
      onClick={(e) => e.preventDefault()}
      className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full border border-neutral-900 cursor-grab"
      title="Glissez ce bouton dans la barre de favoris"
    >
      <BoltIcon className="w-4 h-4" /> Ajouter au Motion Hub
    </a>
  );
}

export default function QuickAdd() {
  usePageMeta("Quick Add · Admin");
  const [searchParams] = useSearchParams();
  const { videos, companies: loadedCompanies, agencies: loadedAgencies, loading, refetch } =
    useVideosWithEntities("admin");
  const { taxonomies } = useTaxonomies();
  const [companies, setCompanies] = useState<Entity[]>([]);
  const [agencies, setAgencies] = useState<Entity[]>([]);
  useEffect(() => setCompanies(loadedCompanies), [loadedCompanies]);
  useEffect(() => setAgencies(loadedAgencies), [loadedAgencies]);

  const [text, setText] = useState(searchParams.get("url") ?? "");
  const [rows, setRows] = useState<Row[]>([]);
  const [analysing, setAnalysing] = useState(false);
  const [saving, setSaving] = useState<number | null>(null);
  const autoStarted = useRef(false);

  const duplicates = useMemo(() => buildDuplicateIndex(videos), [videos]);

  const patchRow = (key: string, patch: Partial<Row> | ((row: Row) => Partial<Row>)) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...(typeof patch === "function" ? patch(r) : patch) } : r)));

  const analyse = async (input = text) => {
    const links = splitLinks(input);
    if (!links.length) return;
    setAnalysing(true);
    const fresh: Row[] = links.map((link, i) => ({
      key: `${Date.now()}-${i}`,
      input: link,
      state: isValidVideoInput(link) ? "loading" : "invalid",
      include: false,
      title: "",
      enterpriseId: "",
      agencyId: "",
      classes: { industries: [], formats: [], styles: [] },
      suggestions: { industries: [], formats: [], styles: [] },
    }));
    setRows(fresh);

    const queue = fresh.filter((r) => r.state === "loading");
    const worker = async () => {
      for (let row = queue.shift(); row; row = queue.shift()) {
        const current = row;
        const source = parseVideoSource(current.input);
        const dup = duplicates.get(source.sourceKey);
        try {
          const info = await enrichVideoLink(current.input);
          const domains = domainsInText(info.description);
          const brand = matchEntity(companies, { names: [...brandNamesFromTitle(info.title), info.authorName], domains });
          const studio = matchEntity(agencies, { names: [info.authorName], domains });
          const suggestions = suggestFromText(taxonomies, {
            title: info.title,
            description: info.description,
            authorName: info.authorName,
          });
          const accepted = (key: TaxonomyKey) =>
            suggestions[key].filter((s) => s.confidence >= AUTO_ACCEPT).map((s) => s.value);
          patchRow(current.key, {
            state: dup ? "duplicate" : "ready",
            include: !dup && !!info.title,
            enriched: info,
            duplicateOf: dup ? { id: dup.id, title: dup.title, status: dup.status } : undefined,
            title: info.title ?? "",
            enterpriseId: brand?.entity.id ?? "",
            agencyId: studio?.entity.id ?? "",
            brandHint: brand ? `Trouvée via ${brand.reason}` : info.authorName ? `Chaîne : ${info.authorName}` : undefined,
            studioHint: studio ? `Trouvé via ${studio.reason}` : undefined,
            suggestions,
            classes: { industries: accepted("industries"), formats: accepted("formats"), styles: accepted("styles") },
          });
        } catch (err) {
          patchRow(current.key, { state: "error", error: err instanceof Error ? err.message : "Analyse impossible" });
        }
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    setAnalysing(false);
  };

  // Links passed through the bookmarklet or a submission start automatically.
  useEffect(() => {
    if (autoStarted.current || loading || !searchParams.get("url")) return;
    // Wait until the catalog is copied into local state so brands can be matched.
    if (loadedCompanies.length && !companies.length) return;
    autoStarted.current = true;
    analyse(searchParams.get("url")!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, companies, loadedCompanies]);

  const selected = rows.filter((r) => r.include && (r.state === "ready" || r.state === "duplicate" || r.state === "error"));
  const missingBrand = selected.filter((r) => !r.enterpriseId).length;

  const createAll = async (status: VideoStatus) => {
    if (status === "published" && missingBrand) {
      notify({
        isSuccess: false,
        message: `${missingBrand} vidéo(s) sans marque`,
        info: "Choisissez une marque pour chaque vidéo, ou créez-les en brouillon.",
      });
      return;
    }
    let done = 0;
    setSaving(0);
    for (const row of selected) {
      patchRow(row.key, { state: "saving" });
      try {
        const info = row.enriched;
        const id = await saveVideo(null, {
          title: row.title.trim() || info?.title || row.input,
          description: info?.description?.slice(0, 2000) ?? "",
          videoURL: parseVideoSource(row.input).url,
          enterpriseId: row.enterpriseId,
          agencyId: row.agencyId,
          status,
          thumbnailURL: info?.thumbnailURL ?? "",
          styleframesURLs: [],
          industries: row.classes.industries,
          formats: row.classes.formats,
          styles: row.classes.styles,
          tags: [],
          durationSec: info?.durationSec,
          releasedAt: info?.releasedAt ? Timestamp.fromDate(info.releasedAt) : null,
        });
        patchRow(row.key, { state: "saved", savedId: id, include: false });
      } catch (err) {
        patchRow(row.key, { state: "error", error: err instanceof Error ? err.message : "Enregistrement impossible" });
      }
      done++;
      setSaving(done / selected.length);
    }
    setSaving(null);
    refetch();
    notify({
      isSuccess: true,
      message: status === "published" ? `${done} vidéo(s) publiée(s)` : `${done} brouillon(s) créé(s)`,
      info: "Ajoutez les styleframes depuis chaque fiche.",
    });
  };

  const onEntityCreated = (entity: Entity, kind: "companies" | "agencies") =>
    (kind === "companies" ? setCompanies : setAgencies)((prev) => [...prev, entity]);

  const linkCount = splitLinks(text).length;

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 md:px-20 px-4">
      <NavBar />
      <div className="max-w-5xl mx-auto px-0 md:px-4 py-8 flex flex-col gap-6 pb-32">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900">
            <ArrowLeftIcon className="w-4 h-4" /> CMS
          </Link>
          <span className="text-neutral-300">/</span>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Quick Add</h1>
        </div>

        <Section
          title="Coller des liens"
          description="Un lien par ligne : vidéos YouTube, Vimeo ou X. Chaque lien devient une fiche pré-remplie (titre, miniature, durée, marque, studio, catégories) que vous validez avant création."
          actions={<Bookmarklet />}
        >
          <textarea
            className={`${inputClass} font-mono text-[13px] leading-relaxed`}
            rows={Math.min(10, Math.max(4, text.split("\n").length + 1))}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={"https://www.youtube.com/watch?v=…\nhttps://vimeo.com/…\nhttps://x.com/…/status/…"}
          />
          <div className="flex flex-wrap items-center gap-3">
            <button className={primaryButton} disabled={!linkCount || analysing || loading} onClick={() => analyse()}>
              <BoltIcon className="w-4 h-4" />
              {analysing ? "Analyse en cours…" : linkCount ? `Analyser ${linkCount} lien${linkCount > 1 ? "s" : ""}` : "Analyser les liens"}
            </button>
            <span className="text-xs text-neutral-500">
              Astuce : glissez le bouton « Ajouter au Motion Hub » dans vos favoris pour envoyer la vidéo ouverte en un clic.
            </span>
          </div>
        </Section>

        {rows.map((row) => (
          <QuickAddCard
            key={row.key}
            row={row}
            companies={companies}
            agencies={agencies}
            taxonomyOptions={taxonomies}
            onChange={(patch) => patchRow(row.key, patch)}
            onEntityCreated={onEntityCreated}
          />
        ))}
      </div>

      {rows.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-[2000] border-t border-neutral-200 bg-white/95 backdrop-blur">
          <div className="max-w-5xl mx-auto px-4 md:px-8 py-3 flex flex-wrap items-center gap-3">
            {saving !== null ? (
              <div className="flex-1 min-w-[200px]">
                <ProgressBar value={saving} label="Création des fiches…" />
              </div>
            ) : (
              <span className="flex-1 text-sm text-neutral-600">
                {selected.length} vidéo{selected.length > 1 ? "s" : ""} sélectionnée{selected.length > 1 ? "s" : ""}
                {missingBrand > 0 && <span className="text-amber-700"> · {missingBrand} sans marque</span>}
              </span>
            )}
            <button className={secondaryButton} disabled={!selected.length || saving !== null || analysing} onClick={() => createAll("draft")}>
              Créer en brouillon
            </button>
            <button className={primaryButton} disabled={!selected.length || saving !== null || analysing} onClick={() => createAll("published")}>
              Publier directement
            </button>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}

function QuickAddCard({
  row,
  companies,
  agencies,
  taxonomyOptions,
  onChange,
  onEntityCreated,
}: {
  row: Row;
  companies: Entity[];
  agencies: Entity[];
  taxonomyOptions: Record<TaxonomyKey, string[]>;
  onChange: (patch: Partial<Row>) => void;
  onEntityCreated: (entity: Entity, kind: "companies" | "agencies") => void;
}) {
  const info = row.enriched;

  if (row.state === "invalid") {
    return (
      <div className="bg-white border border-red-200 rounded-lg px-5 py-3 text-sm text-red-800 flex items-center gap-2">
        <ExclamationTriangleIcon className="w-4 h-4" /> « {row.input} » n'est pas un lien valide.
      </div>
    );
  }
  if (row.state === "loading") {
    return (
      <div className="bg-white border border-neutral-200 rounded-lg p-5 flex gap-5 animate-pulse">
        <div className="w-56 aspect-video bg-neutral-200 rounded" />
        <div className="flex-1 flex flex-col gap-3">
          <div className="h-4 bg-neutral-200 rounded w-2/3" />
          <div className="h-3 bg-neutral-200 rounded w-1/3" />
          <p className="text-xs text-neutral-500 break-all">{row.input}</p>
        </div>
      </div>
    );
  }
  if (row.state === "saved") {
    return (
      <div className="bg-white border border-emerald-200 rounded-lg px-5 py-3 text-sm flex flex-wrap items-center gap-3">
        <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
        <span className="font-medium">{row.title}</span>
        <Link to={`/admin/video/${row.savedId}`} className="ml-auto underline">
          Compléter la fiche (styleframes, script) →
        </Link>
      </div>
    );
  }

  const toggleClass = (key: TaxonomyKey, value: string) =>
    onChange({
      classes: {
        ...row.classes,
        [key]: row.classes[key].includes(value)
          ? row.classes[key].filter((v) => v !== value)
          : [...row.classes[key], value],
      },
    });

  return (
    <article
      className={`bg-white border rounded-lg p-5 flex flex-col md:flex-row gap-5 transition-opacity ${
        row.include ? "border-neutral-400" : "border-neutral-200 opacity-80"
      } ${row.state === "saving" ? "opacity-50 pointer-events-none" : ""}`}
    >
      <div className="md:w-56 shrink-0 flex flex-col gap-2">
        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
          <input
            type="checkbox"
            checked={row.include}
            onChange={(e) => onChange({ include: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300 text-neutral-900 focus:ring-neutral-900"
          />
          Inclure
        </label>
        {info?.thumbnailURL ? (
          <img src={info.thumbnailURL} alt="" className="w-full aspect-video object-cover rounded bg-neutral-100" />
        ) : (
          <div className="w-full aspect-video rounded bg-neutral-100 flex items-center justify-center text-xs text-neutral-500">
            Pas de miniature
          </div>
        )}
        <a href={row.input} target="_blank" rel="noreferrer" className="text-xs text-neutral-500 underline break-all">
          {row.input}
        </a>
        {info?.durationSec && (
          <span className="text-xs text-neutral-600 tabular-nums">
            Durée {Math.floor(info.durationSec / 60)}:{String(info.durationSec % 60).padStart(2, "0")}
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-4">
        {row.state === "duplicate" && row.duplicateOf && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex flex-wrap items-center gap-2">
            Déjà dans le catalogue :
            <Link to={`/admin/video/${row.duplicateOf.id}`} className="font-medium underline">
              {row.duplicateOf.title || "sans titre"}
            </Link>
            <StatusBadge status={row.duplicateOf.status} />
          </div>
        )}
        {row.state === "error" && <p className="text-sm text-red-700">{row.error}</p>}
        {info?.warnings.map((w) => (
          <p key={w} className="text-sm text-amber-800">{w}</p>
        ))}

        <input
          className={`${inputClass} text-base font-medium`}
          value={row.title}
          placeholder="Titre de la vidéo"
          onChange={(e) => onChange({ title: e.target.value })}
        />
        <div className="grid sm:grid-cols-2 gap-4">
          <EntityPicker
            label="Marque"
            kind="companies"
            companies={companies}
            agencies={agencies}
            value={row.enterpriseId}
            onChange={(id) => onChange({ enterpriseId: id })}
            onCreated={onEntityCreated}
            hint={row.brandHint}
            suggestedName={brandNamesFromTitle(row.title)[0]}
          />
          <EntityPicker
            label="Studio"
            kind="agencies"
            companies={companies}
            agencies={agencies}
            value={row.agencyId}
            onChange={(id) => onChange({ agencyId: id })}
            onCreated={onEntityCreated}
            hint={row.studioHint}
            suggestedName={info?.authorName}
          />
        </div>

        <div className="flex flex-col gap-2">
          {(Object.keys(TAXONOMY_LABELS) as TaxonomyKey[]).map((key) => {
            const values = Array.from(new Set([...row.classes[key], ...row.suggestions[key].map((s) => s.value)]));
            return (
              <div key={key} className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-neutral-500 w-20 shrink-0">{TAXONOMY_LABELS[key]}</span>
                {values.length === 0 && <span className="text-xs text-neutral-400">Aucune suggestion</span>}
                {values.map((value) => {
                  const on = row.classes[key].includes(value);
                  const reason = row.suggestions[key].find((s) => s.value === value)?.reason;
                  return (
                    <button
                      type="button"
                      key={value}
                      title={reason ? `Suggestion : ${reason}` : undefined}
                      onClick={() => toggleClass(key, value)}
                      className={`text-xs px-2.5 py-1 rounded-full border ${
                        on ? "bg-neutral-900 text-white border-neutral-900" : "bg-violet-50 text-violet-900 border-dashed border-violet-300"
                      }`}
                    >
                      {value}
                    </button>
                  );
                })}
                <select
                  className="text-xs border border-neutral-200 rounded-full px-2 py-1 bg-white text-neutral-600"
                  value=""
                  onChange={(e) => e.target.value && toggleClass(key, e.target.value)}
                  aria-label={`Ajouter : ${TAXONOMY_LABELS[key]}`}
                >
                  <option value="">+ ajouter</option>
                  {taxonomyOptions[key]
                    .filter((v) => !row.classes[key].includes(v))
                    .map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                </select>
              </div>
            );
          })}
        </div>
      </div>
    </article>
  );
}
